import React, { FC, useState } from "react";
import { Link, Notification, Button, Toggle, Text, Icon, Checkbox } from "@telia/teddy";
import _find from "lodash/find";
import { connect } from "react-redux";
import { Tooltip } from "react-tooltip";
import ProductUserController from "./ProductUserController";
import { FORM_ID } from "../../../../../../constants";
import { Form, Modal, RootState } from "../../../../../../models";
import { FieldSet, FormRow, FormRowFormats, Input } from "../../../../forms";
import { getModals, isSecretNumberModal } from "../../../../modals/state/selectors";
import { RapidForm } from "../../../../rapidform";
import { VALIDATION_FIELD } from "../../../../rapidform/formValidation";
import {
    isSecretNumber as isSecretNumberSelector,
    isZipNeeded as isZipNeededSelector,
    isNotFound as isNotFoundSelector,
} from "../../../state/selectors/customerInfoSelectors";
import {
    getSubscriptionMaxAgeAllowed,
    getSubscriptionName,
    isChildSubscription as isChildSubscriptionSelector,
    isJuniorSubscription as isJuniorSubscriptionSelector,
    isProductUserMandatory as isProductUserMandatorySelector,
    isSubscriptionPrepaid as isSubscriptionPrepaidSelector,
    isServiceSubscription as isServiceSubscriptionSelector,
    isTeliaTryggSubscription as isTeliaTryggSubscriptionSelector,
} from "../../../state/selectors/subscriptionsSelectors";
import TEXT from "../../../text-consts";
import SubscriptionAgeNotOkLightAlert from "../../atoms/SubscriptionAgeNotOkLightAlert/SubscriptionAgeNotOkLightAlert";
import SubscriptionBenefitsYoung from "../../atoms/SubscriptionBenefitsYoung/SubscriptionBenefitsYoung";
import ContentfulModal from "../../molecules/ContentfulModal/ContentfulModal";
import "./MemberForm.less";

interface StateProps {
    isChildSubscription: boolean;
    isJuniorSubscription: boolean;
    isNotFound: boolean;
    isProductUserMandatory: boolean;
    isSecretNumber: boolean;
    isServiceSubscription: boolean;
    isSubscriptionPrepaid: boolean;
    isTeliaTryggSubscription: boolean;
    isZipNeeded: boolean;
    maxAgeAllowed?: number;
    secretNumberModal: Modal | undefined;
    subscriptionName: string;
}

interface MemberFormProps extends StateProps {
    campaignId?: string;
    isBarringsUsed: boolean;
    isCampaignAllowed: boolean;
    isCampaignToggled: boolean;
    isCatalogListingOk: boolean;
    isEmailTakenForTeliaSky?: boolean;
    isEmailTakenForTeliaTrygg?: boolean;
    isOptingOutForTeliaXServices?: boolean;
    isPostCodeFilled: boolean;
    isProductUserAgeOk: boolean;
    isProductUserChild: boolean;
    isProductUserDifferentThanLegalOwner: boolean;
    isProductUserYoung: boolean;
    memberForm: Form;
    onSubmitMemberDetails: () => void;
    setPersonalDetailsValue: (field: string, value: boolean) => void;
}

const MemberForm: FC<MemberFormProps> = ({
    // Redux (data)
    maxAgeAllowed,
    isChildSubscription,
    isJuniorSubscription,
    isProductUserMandatory,
    isSubscriptionPrepaid,
    isServiceSubscription,
    isTeliaTryggSubscription,
    secretNumberModal,
    isNotFound,
    isZipNeeded,
    // Own props
    subscriptionName,
    isPostCodeFilled,
    memberForm,
    campaignId,
    isBarringsUsed,
    isCampaignToggled,
    isCampaignAllowed,
    isCatalogListingOk,
    isProductUserAgeOk,
    isProductUserDifferentThanLegalOwner,
    isProductUserChild,
    isProductUserYoung,
    isSecretNumber,
    isEmailTakenForTeliaTrygg,
    isEmailTakenForTeliaSky,
    isOptingOutForTeliaXServices,
    setPersonalDetailsValue,
    onSubmitMemberDetails,
}) => {
    const [openSecretNumberModal, setOpenSecretNumberModal] = useState(false);
    const streetAddress = memberForm.values?.member
        ? memberForm.values.member[VALIDATION_FIELD.PRODUCT_USER_STREET]
        : undefined;
    const zipCode = memberForm.values?.member
        ? memberForm.values.member[VALIDATION_FIELD.PRODUCT_USER_POSTCODE]
        : undefined;

    return (
        <div className="member-form">
            <RapidForm
                id={FORM_ID.ADD_MEMBER}
                action={"#"}
                errors={memberForm.errors}
                values={memberForm.values}
                visible={memberForm.visible}
                started={memberForm.started}
                onSubmit={onSubmitMemberDetails}
            >
                <FieldSet name="member">
                    <div className="member-form__checkbox" data-cy="member-form-checkboxes">
                        {!isProductUserMandatory && (
                            <ProductUserController
                                checked={!isProductUserDifferentThanLegalOwner}
                                setValue={(value) =>
                                    setPersonalDetailsValue("isProductUserDifferentThanLegalOwner", value)
                                }
                            />
                        )}
                        <div className="member-form__number-checkbox">
                            {!isServiceSubscription && (
                                <Checkbox className="checkbox">
                                    <Checkbox.Trigger
                                        checked={!isCatalogListingOk || isSecretNumber}
                                        onCheckedChange={() => {
                                            setPersonalDetailsValue("isCatalogListingOk", !isCatalogListingOk);
                                        }}
                                    />
                                    <span>{TEXT.PERSONAL_DETAILS.NUMBER_SHOULD_NOT_BE_LISTED_IN_CATALOG}</span>
                                </Checkbox>
                            )}
                            {!isServiceSubscription && (
                                <div className="member-form__secret_number">
                                    <Checkbox className="checkbox">
                                        <Checkbox.Trigger
                                            checked={isSecretNumber}
                                            onCheckedChange={() => {
                                                setPersonalDetailsValue("isSecretNumber", !isSecretNumber);

                                                if (!isSecretNumber) {
                                                    setPersonalDetailsValue("isCatalogListingOk", true);
                                                }
                                            }}
                                        >
                                            <Checkbox.Indicator />
                                        </Checkbox.Trigger>
                                        <span>{TEXT.PERSONAL_DETAILS.SECRET_NUMBER}</span>
                                    </Checkbox>
                                    <div className="member-form__secret_number_link">
                                        <Button
                                            variant="text-underline"
                                            type="button"
                                            onClick={() => setOpenSecretNumberModal(true)}
                                        >
                                            Les mer
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    {(isProductUserDifferentThanLegalOwner || isChildSubscription || isJuniorSubscription) && (
                        <>
                            {/* TODO: Border: solid 1px #6E6E6E in Styleguide or override */}
                            <Input
                                type="text"
                                name={VALIDATION_FIELD.FIRST_NAME}
                                label={TEXT.PERSONAL_DETAILS.FIRSTNAME}
                                data-di-mask
                            />
                            <Input
                                type="text"
                                name={VALIDATION_FIELD.LAST_NAME}
                                label={TEXT.PERSONAL_DETAILS.LASTNAME}
                                data-di-mask
                            />
                            <FormRow format={FormRowFormats.FORTY_SIXTY} data-di-mask>
                                <Input
                                    type="tel"
                                    name={VALIDATION_FIELD.BIRTHDATE}
                                    label={TEXT.PERSONAL_DETAILS.BIRTHDATE}
                                    placeholder="DD.MM.ÅÅÅÅ"
                                    data-di-mask
                                />
                            </FormRow>
                            {streetAddress && (
                                <Input
                                    type="text"
                                    name={VALIDATION_FIELD.PRODUCT_USER_STREET}
                                    label={TEXT.PERSONAL_DETAILS.STREET}
                                    data-di-mask
                                    disabled
                                />
                            )}
                            {(isZipNeeded || zipCode) && (
                                <>
                                    {!isNotFound && isZipNeeded && (
                                        <Notification type="tile" variant="information" mt="200">
                                            <Notification.Icon />
                                            <Notification.Text>
                                                Vi klarte ikke hente ut informasjon i folkeregisteret basert på
                                                fødselsdato og navn, og trenger derfor postnummer for å kunne
                                                identifisere riktig person. Postnummeret må samsvare med hva som er
                                                registrert i Folkeregisteret. Vennligst se over og prøv igjen.
                                            </Notification.Text>
                                        </Notification>
                                    )}
                                    {isNotFound && (
                                        <Notification type="tile" variant="warning" mt="200">
                                            <Notification.Icon />
                                            <Notification.Text>
                                                Vi klarte ikke hente ut informasjon i folkeregisteret basert på
                                                fødselsdato, navn og postnummer. Vennligst sjekk at opplysningene er
                                                korrekte og samsvarer med hva som er registrert i{" "}
                                                <Link
                                                    href="https://www.skatteetaten.no/skjema/opplysninger-i-folkeregisteret/"
                                                    variant="text"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Folkeregisteret
                                                </Link>
                                                , og prøv igjen.
                                            </Notification.Text>
                                        </Notification>
                                    )}
                                    <FormRow format={FormRowFormats.FORTY_SIXTY} data-di-mask>
                                        <Input
                                            type="text"
                                            name={VALIDATION_FIELD.PRODUCT_USER_POSTCODE}
                                            label={TEXT.PERSONAL_DETAILS.POSTCODE}
                                        />
                                        <Input
                                            disabled={isPostCodeFilled}
                                            type="text"
                                            name={VALIDATION_FIELD.PRODUCT_USER_CITY}
                                            label={TEXT.PERSONAL_DETAILS.CITY}
                                        />
                                    </FormRow>
                                </>
                            )}
                        </>
                    )}
                    {!isProductUserDifferentThanLegalOwner &&
                        !(isEmailTakenForTeliaTrygg || isEmailTakenForTeliaSky) && (
                            <Input
                                type="email"
                                name={VALIDATION_FIELD.PRODUCT_USER_EMAIL}
                                label={TEXT.PERSONAL_DETAILS.EMAIL}
                                placeholder="ola@nordmann.no"
                                helpText={
                                    isTeliaTryggSubscription ? TEXT.PERSONAL_DETAILS.EMAIL_HELP_TEXT_TELIA_TRYGG : ""
                                }
                                data-di-mask
                            />
                        )}
                    {isProductUserChild && !isJuniorSubscription && (
                        <div className="member-form__barrings">
                            <div className="member-form__toggle-container">
                                <Toggle id="toggle-button-barrings" data-di-mask>
                                    <Toggle.Label htmlFor="toggle-button-barrings">
                                        {TEXT.ADD_MEMBER.BARRINGS_FOR_CHILDREN}
                                    </Toggle.Label>
                                    <Toggle.Input
                                        onCheckedChange={() => {
                                            setPersonalDetailsValue("isBarringsUsed", !isBarringsUsed);
                                        }}
                                        checked={isBarringsUsed}
                                    >
                                        <Toggle.Indicator />
                                        <Toggle.Thumb />
                                    </Toggle.Input>
                                </Toggle>
                            </div>
                            <button
                                type="button"
                                className="member-form__barrings-tooltip-trigger"
                                data-tooltip-id="member-form-tooltip"
                                data-tooltip-place="top"
                                data-tooltip-content={TEXT.ADD_MEMBER.BARRINGS_TOOLTIP}
                                aria-label="Informasjon om sperrer for barn"
                            >
                                <Icon
                                    name="info"
                                    className="member-form__barrings-icon"
                                    aria-hidden="true"
                                    focusable="false"
                                />
                            </button>
                            <Tooltip className="member-form__tooltip" place="top" id="member-form-tooltip" />
                        </div>
                    )}
                    {isProductUserYoung &&
                        !isChildSubscription &&
                        !isJuniorSubscription &&
                        !isSubscriptionPrepaid &&
                        !isServiceSubscription && <SubscriptionBenefitsYoung />}
                    {(isProductUserDifferentThanLegalOwner || isEmailTakenForTeliaTrygg || isEmailTakenForTeliaSky) && (
                        <>
                            <Input
                                type="email"
                                name={VALIDATION_FIELD.PRODUCT_USER_EMAIL}
                                label={TEXT.PERSONAL_DETAILS.EMAIL}
                                placeholder="ola@nordmann.no"
                                helpText={
                                    isTeliaTryggSubscription ? TEXT.PERSONAL_DETAILS.EMAIL_HELP_TEXT_TELIA_TRYGG : ""
                                }
                                data-di-mask
                            />
                            {(isEmailTakenForTeliaTrygg || isEmailTakenForTeliaSky) && (
                                <>
                                    <Notification type="tile" variant="warning" className="subscription-age-not-ok">
                                        <Notification.Icon />
                                        <Notification.Text>
                                            {TEXT.ADD_MEMBER.TELIA_X_SERVICE.UNIQUE_EMAIL(
                                                isEmailTakenForTeliaTrygg,
                                                isEmailTakenForTeliaSky,
                                                isTeliaTryggSubscription,
                                            )}
                                        </Notification.Text>
                                    </Notification>
                                    {!isTeliaTryggSubscription && (
                                        <Checkbox className="checkbox">
                                            <Checkbox.Trigger
                                                checked={!!isOptingOutForTeliaXServices}
                                                onCheckedChange={() => {
                                                    setPersonalDetailsValue(
                                                        "isOptingOutForTeliaXServices",
                                                        !isOptingOutForTeliaXServices,
                                                    );
                                                }}
                                            />
                                            <span>
                                                {TEXT.PERSONAL_DETAILS.ACTIVATE_TELIA_TRYGG_LATER(
                                                    isEmailTakenForTeliaTrygg,
                                                    isEmailTakenForTeliaSky,
                                                )}
                                            </span>
                                        </Checkbox>
                                    )}
                                </>
                            )}
                        </>
                    )}
                    {campaignId && (
                        <div className="member-form__campaign-wrapper">
                            <img
                                width={120}
                                className="member-form__img-campaign"
                                alt={TEXT.ADD_MEMBER.CAMPAIGN[campaignId].IMG_ALT}
                                src={TEXT.ADD_MEMBER.CAMPAIGN[campaignId].IMG_SRC}
                            />
                            <Text as="p">{TEXT.ADD_MEMBER.CAMPAIGN[campaignId].DESCRIPTION(subscriptionName)}</Text>
                            {!isCampaignAllowed && (
                                <Notification className="subscription-age-not-ok" type="tile" variant="warning">
                                    <Notification.Icon name="info" />
                                    <Notification.Text>
                                        {TEXT.ADD_MEMBER.CAMPAIGN[campaignId].NOT_ALLOWED}
                                    </Notification.Text>
                                </Notification>
                            )}
                            {isCampaignAllowed && (
                                <div className="member-form__checkbox">
                                    <Checkbox className="checkbox">
                                        <Checkbox.Trigger
                                            onCheckedChange={() => {
                                                setPersonalDetailsValue("isCampaignToggled", !isCampaignToggled);
                                            }}
                                            checked={isCampaignToggled}
                                        >
                                            <Checkbox.Indicator />
                                        </Checkbox.Trigger>

                                        <span>{TEXT.ADD_MEMBER.CAMPAIGN[campaignId].OPTION}</span>
                                    </Checkbox>
                                </div>
                            )}
                            <span className="member-form__campaign-disclaimer">
                                {TEXT.ADD_MEMBER.CAMPAIGN[campaignId].DISCLAIMER}
                            </span>
                        </div>
                    )}
                </FieldSet>
                {!isProductUserAgeOk && <SubscriptionAgeNotOkLightAlert maxAgeAllowed={maxAgeAllowed} />}
            </RapidForm>
            {secretNumberModal && (
                <ContentfulModal
                    open={openSecretNumberModal}
                    setOpen={setOpenSecretNumberModal}
                    modal={secretNumberModal}
                />
            )}
        </div>
    );
};

const mapStateToProps: (state: RootState, ownProps: { selectedSubscriptionId: string }) => StateProps = (
    state,
    ownProps,
) => ({
    subscriptionName: getSubscriptionName(state, ownProps.selectedSubscriptionId),
    isSubscriptionPrepaid: isSubscriptionPrepaidSelector(state, ownProps.selectedSubscriptionId),
    maxAgeAllowed: getSubscriptionMaxAgeAllowed(state, ownProps.selectedSubscriptionId),
    isChildSubscription: isChildSubscriptionSelector(state, ownProps.selectedSubscriptionId),
    isJuniorSubscription: isJuniorSubscriptionSelector(state, ownProps.selectedSubscriptionId),
    isProductUserMandatory: isProductUserMandatorySelector(state, ownProps.selectedSubscriptionId),
    isServiceSubscription: isServiceSubscriptionSelector(state, ownProps.selectedSubscriptionId),
    isTeliaTryggSubscription: isTeliaTryggSubscriptionSelector(state, ownProps.selectedSubscriptionId),
    isSecretNumber: isSecretNumberSelector(state),
    secretNumberModal: _find(getModals(state), isSecretNumberModal),
    isNotFound: isNotFoundSelector(state),
    isZipNeeded: isZipNeededSelector(state),
});

export default connect(mapStateToProps)(MemberForm);
